/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  NgZone,
  ViewChild,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { KEY_DOWN, KEY_UP } from '@gh/core-ui';
import { denull } from '@gh/core-util';
import { first$ } from '@gh/rx/operators';
import { Column, ICellEditorParams } from 'ag-grid-community';
import { includes } from 'lodash';
import { EditableCellOption, EditableCellModel } from '../../../state';
import { createCellFormControl } from '../../grid-cell.utils';
import { GridCellEditorComponent } from '../../grid-component.interfaces';
import { MatSelect } from '@angular/material/select';

const STOPPED_KEYS = [KEY_UP, KEY_DOWN];

@Component({
  standalone: false,
  selector: 'gh-grid-select-cell-editor',
  styleUrls: ['grid-select-cell-editor.component.scss'],
  templateUrl: 'grid-select-cell-editor.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GridSelectCellEditorComponent implements GridCellEditorComponent, AfterViewInit {
  private static readonly MAX_PANEL_WIDTH_PX = 640;
  private static readonly VIEWPORT_WIDTH_CAP = 0.96;
  private static readonly FALLBACK_CHAR_WIDTH_PX = 8;
  private static readonly OPTION_EXTRA_PX = 56;
  private static readonly MEASURE_FONT =
    '14px system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

  required: boolean = false;

  control: FormControl;
  options: EditableCellOption[];
  editorOptions?: any;

  @ViewChild(MatSelect, { static: false }) select: MatSelect;
  @ViewChild('select', { read: ElementRef, static: false }) private selectHost?: ElementRef<HTMLElement>;

  panelWidth: string | null = null;

  private eGridCell: HTMLElement | undefined;
  private columnActualWidthPx = 0;
  private stopEditing: () => void;

  constructor(private ngZone: NgZone, private cdr: ChangeDetectorRef) {
  }

  ngAfterViewInit(): void {
    this.ngZone.onMicrotaskEmpty.pipe(first$()).subscribe(() => {
      this.setPanelWidthFromTriggerAndOptions();
      this.cdr.detectChanges();
      this.select.focus();
      this.select.open();
    });
  }

  agInit(params: ICellEditorParams): void {
    const model: EditableCellModel = params['modelFactory']({
      node: params.node,
      colDef: params.column.getColDef(),
      data: params.node.data,
    });

    this.options = params['optionSet'].getOptions({
      node: params.node,
      colDef: params.column.getColDef(),
      data: params.node.data,
    });

    this.required = model.required;
    this.control = createCellFormControl(model);
    this.stopEditing = params.stopEditing;
    this.eGridCell = params.eGridCell as HTMLElement | undefined;
    const column = params.column as Column;
    this.columnActualWidthPx =
      typeof column?.getActualWidth === 'function' ? column.getActualWidth() : 0;
  }

  getValue(): any {
    return denull(this.control.value);
  }

  onClose(): void {
    this.ngZone.onMicrotaskEmpty.pipe(first$()).subscribe(() => {
      this.stopEditing();
    });
  }

  onBlur(): void {
    this.stopEditing();
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (includes(STOPPED_KEYS, event.keyCode)) {
      event.stopPropagation();
    }
  }

  private setPanelWidthFromTriggerAndOptions(): void {
    const triggerWidth = this.getTriggerWidthPx();
    const columnCellWidth = this.getColumnCellWidthPx();
    const labelsWidth = this.measureMaxOptionLabelWidth();
    const contentWidth = labelsWidth + GridSelectCellEditorComponent.OPTION_EXTRA_PX;
    const viewportCap = Math.min(
      GridSelectCellEditorComponent.MAX_PANEL_WIDTH_PX,
      typeof window !== 'undefined'
        ? window.innerWidth * GridSelectCellEditorComponent.VIEWPORT_WIDTH_CAP
        : GridSelectCellEditorComponent.MAX_PANEL_WIDTH_PX,
    );
    const minFromColumnAndTrigger = Math.max(columnCellWidth, triggerWidth);
    const w = Math.min(Math.max(minFromColumnAndTrigger, contentWidth), viewportCap);
    this.panelWidth = `${Math.ceil(w)}px`;
  }

  private getColumnCellWidthPx(): number {
    const fromCell = this.eGridCell?.getBoundingClientRect().width ?? 0;
    if (fromCell > 0) {
      return fromCell;
    }
    const host = this.selectHost?.nativeElement;
    const fromClosest =
      host?.closest<HTMLElement>('.ag-cell')?.getBoundingClientRect().width ?? 0;
    if (fromClosest > 0) {
      return fromClosest;
    }
    return this.columnActualWidthPx > 0 ? this.columnActualWidthPx : 0;
  }

  private getTriggerWidthPx(): number {
    const root = this.selectHost?.nativeElement;
    if (!root) {
      return 0;
    }
    const inner = root.querySelector<HTMLElement>('.mat-mdc-select-trigger');
    return (inner ?? root).getBoundingClientRect().width;
  }

  private measureMaxOptionLabelWidth(): number {
    if (!this.options?.length) {
      return 0;
    }
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return this.options.reduce((max, o) => {
        const text = o.name != null ? String(o.name) : '';
        return Math.max(max, text.length * GridSelectCellEditorComponent.FALLBACK_CHAR_WIDTH_PX);
      }, 0);
    }
    ctx.font = GridSelectCellEditorComponent.MEASURE_FONT;
    return this.options.reduce((max, o) => {
      const text = o.name != null ? String(o.name) : '';
      return Math.max(max, ctx.measureText(text).width);
    }, 0);
  }
}
